// XLSelect - Sprint 44 XL.
// Select nativo estilizado. Accesible por defecto (element nativo).
// Opciones como array [{value, label}] o children. Dark mode automatico.
import { forwardRef, useEffect, useId } from 'react';
import { ensureKeyframes, injectCSS } from '../../design/motion';

const CSS_ID = 'xl-select-css';
const CSS = `
.xl-sel-wrap { position: relative; display: inline-flex; width: 100% }
.xl-select {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  width: 100%;
  font: 400 14px/1.4 var(--xl-font-sans);
  color: var(--xl-color-text);
  background: var(--xl-color-surface);
  border: 1px solid var(--xl-color-border);
  border-radius: 8px;
  padding: 10px 36px 10px 12px;
  box-sizing: border-box;
  cursor: pointer;
  transition: border-color 120ms cubic-bezier(0,0,0.2,1),
              box-shadow   120ms cubic-bezier(0,0,0.2,1);
}
.xl-select:hover { border-color: rgba(46, 55, 69, 0.25) }
.xl-select:focus {
  outline: none;
  border-color: var(--xl-color-focus);
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.25);
}
[data-xl-theme="dark"] .xl-select:focus {
  box-shadow: 0 0 0 3px rgba(96,165,250,0.28);
}
.xl-select:disabled {
  background: var(--xl-color-surface-alt);
  color: var(--xl-color-text-muted);
  cursor: not-allowed;
}
.xl-sel-caret {
  position: absolute; right: 12px; top: 50%;
  transform: translateY(-50%);
  width: 0; height: 0;
  border-left: 4px solid transparent;
  border-right: 4px solid transparent;
  border-top: 5px solid var(--xl-color-text-secondary);
  pointer-events: none;
}
`;

const XLSelect = forwardRef(function XLSelect({
  label,
  options,
  children,
  id,
  className = '',
  hint,
  error,
  ...rest
}, ref) {
  useEffect(() => {
    ensureKeyframes();
    injectCSS(CSS_ID, CSS);
  }, []);

  const autoId = useId();
  const fieldId = id || ('xl-sel-' + autoId);

  return (
    <label className={'xl-field ' + className} htmlFor={fieldId}>
      {label && <span className="xl-field__label">{label}</span>}
      <span className="xl-sel-wrap">
        <select
          {...rest}
          id={fieldId}
          ref={ref}
          aria-invalid={error ? 'true' : undefined}
          className={'xl-select ' + (error ? 'xl-input--error' : '')}
        >
          {options
            ? options.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))
            : children}
        </select>
        <span className="xl-sel-caret" aria-hidden="true" />
      </span>
      {!error && hint && (
        <span className="xl-field__hint">{hint}</span>
      )}
      {error && (
        <span className="xl-field__error" role="alert">{error}</span>
      )}
    </label>
  );
});

export default XLSelect;
